'use client'
import Link from 'next/link'
import { memo } from 'react'

import IsSaleButton from '@/components/map/components/filterSearchBar/components/IsSaleButton'
import useFilterSearchBarViewModel from '@/components/map/components/filterSearchBar/viewModel'
import type { useNaverMapViewModelProps } from '@/components/map/components/naverMap/viewModel'

import Cover from '@/shared/components/Cover'
import Icons from '@/shared/components/icon/Icons'
import TopBanner from '@/shared/components/TopBanner'
import { Caption, Text, Title } from '@/shared/components/typography'
import VersionInfo from '@/shared/components/VersionInfo'

export interface SearchBarType {
  searchKey: string
  searchWord: string
}

export interface FilterSearchBarProps extends Pick<useNaverMapViewModelProps, 'isFilterOn' | 'setIsFilterOn'> {
  appEnv?: string
}

function FilterSearchBar(props: FilterSearchBarProps) {
  const { states, setters, handlers, texts } = useFilterSearchBarViewModel({
    isFilterOn: props.isFilterOn,
    setIsFilterOn: props.setIsFilterOn
  })

  const isNotProd = props.appEnv !== 'prod'

  return (
    <>
      <Cover id="layer3" isOpen={states.isShowTip} className="px-[16px] pb-[12px] pt-[4px]">
        <nav className="flex h-[56px] w-full items-center">
          <div className="mr-[8px] h-[24px] w-[24px] cursor-pointer" onClick={() => setters.setIsShowTip(false)}>
            <Icons.BackIcon stroke="currentColor" color="#263238" />
          </div>
          <div className="h-[24px] w-full text-[16px] leading-[24px] text-gray-080">주차권 핀 설명</div>
        </nav>
        <div className="mx-auto my-[32px] max-w-[390px]">
          <div className="flex w-full flex-col space-y-[32px]">
            {PIN_DESCRIPTION_DATA.map(({ id, icon, title, description }) => (
              <div className="flex items-start space-x-5" key={id}>
                <div className="h-[56px] w-[56px] flex-none">{icon}</div>
                <div className="flex flex-col justify-between">
                  <Title level="text3" color="text090" weight="bold">
                    {title}
                  </Title>
                  <Text level="caption1" color="text090">
                    {description}
                  </Text>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="flex w-full items-center justify-center space-x-3 border-t-[1px] border-030 pt-[18px]">
          <Icons.CautionIcon fill="#B8C3D0" />
          <Text level="caption2" color="text070">
            주차핀 속 가격은 1시간 기준입니다.
          </Text>
        </div>
      </Cover>

      <div className="absolute left-0 top-0 z-20 flex w-full flex-col">
        <TopBanner />
        <div className={`mx-[16px] mb-[12px] mt-[6px]`}>
          <div className="flex flex-col gap-[10px] rounded-[10px] bg-gray-010 px-5 py-3 shadow-120">
            <div className="flex w-full items-center justify-between">
              <Link href={`/map/keyword`} className="flex w-full items-center gap-[6px]">
                <Icons.SearchIcon stroke="currentColor" color="#B8C3D0" />
                <div className="text-gray-050">{texts.placeholder}</div>
              </Link>
              <div className="relative flex items-center justify-center">
                <Icons.ModuLogo />
                {isNotProd && <VersionInfo appEnv={props.appEnv ?? 'local'} />}
              </div>
            </div>
            <div className="flex w-full justify-between">
              <div className="flex w-full gap-2">
                {states.buttons.map((button) => (
                  <IsSaleButton
                    key={button.id}
                    text={button.text}
                    isActive={button.id === states.activeIndex}
                    onClick={() => handlers.onFilterButtonClick(button.id)}
                  />
                ))}
              </div>
              <div
                className="flex w-full cursor-pointer items-center justify-end gap-1"
                onClick={() => setters.setIsShowTip(true)}
              >
                <Icons.HelpIcon fill="none" color="#6D7D90" className="mb-[2px]" />
                <Caption level="caption2" color="text070">
                  핀 설명
                </Caption>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  )
}

const PIN_DESCRIPTION_DATA = [
  {
    id: 1,
    icon: <Icons.PartnerPinIcon />,
    title: '주차권 판매 주차장',
    description: '1회 주차 이용권 구매가 가능한 주차장입니다.'
  },
  {
    id: 2,
    icon: <Icons.SharePinIcon />,
    title: '공유주차장',
    description: '이웃이 공유한 집 또는 가게 앞 주차 공간입니다. 결제 후 주차 가능합니다.'
  },
  {
    id: 3,
    icon: <Icons.NormalPinIcon />,
    title: '일반주차장',
    description: '앱 결제를 지원하지 않습니다. 주차장 정보 확인이 가능합니다.'
  }
]

export default memo(FilterSearchBar)
